import { TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';
import { queryOptionsGetTasksPaginated } from '../api/task/taskApi';
import { useExcludedTaskCounts } from '../hooks/useExcludedTaskCounts';
import { useTaskPartyNames } from '../hooks/useTaskPartyNames';
import {
  HOME_VISIBLE_TASKS,
  defaultTaskSortBy,
} from '../pages/v3/Tasks/taskTabs';
import { WorkspaceTask } from '../types/taskTypes';
import { toTitleCase } from '../utils/helpers/common';
import { EntityList } from './EntityList';
import { SingleQueryResults } from './QueryResults/SingleQueryResults';
import { SnapshotListSkeleton } from './SnapshotListSkeleton';
import { TaskCard, TaskCardSkeleton, buildTaskCardViewUrl } from './TaskCard';
import { TaskSnapshotViewAll } from './TaskSnapshotViewAll';

export type CompetingClaimTask = WorkspaceTask;

const TASK_TYPE = TaskTypeEnum.CompetingClaim;

const CompetingClaimsSkeleton = () => (
  <SnapshotListSkeleton
    label="Loading competing claim tasks"
    hasTitle
    numCards={HOME_VISIBLE_TASKS}
    renderCard={() => <TaskCardSkeleton type={TASK_TYPE} />}
  />
);

const CompetingClaimContent = ({
  countedTaskCount,
  tasks,
  totalTasks,
}: {
  /** Shown on the badge: the total less any resolved claims. */
  countedTaskCount: number;
  tasks: CompetingClaimTask[];
  /** Everything listable, resolved included, so "View All" still offers them. */
  totalTasks: number;
}) => {
  const { search } = useLocation();
  const { workspaceId } = useParams();
  const { getPartyNames, isWorkspaceTypePending } = useTaskPartyNames(
    tasks,
    workspaceId,
  );

  if (isWorkspaceTypePending) {
    return <CompetingClaimsSkeleton />;
  }

  return (
    <EntityList>
      <EntityList.Header>
        <EntityList.Title>{toTitleCase(TASK_TYPE)} Tasks</EntityList.Title>
        <EntityList.Count count={countedTaskCount} />
        <TaskSnapshotViewAll
          segment="competing"
          totalTasks={totalTasks}
          workspaceId={workspaceId}
        />
      </EntityList.Header>

      {tasks.length === 0 ? (
        <EntityList.Empty
          title="There are no competing claims"
          description="If any of your titles receive a competing claim you’ll see it displayed here and on the tasks page."
        />
      ) : (
        <EntityList.Content>
          {tasks.map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              type={TASK_TYPE}
              {...getPartyNames(task)}
              viewUrl={buildTaskCardViewUrl(task, TASK_TYPE, search)}
            />
          ))}
        </EntityList.Content>
      )}
    </EntityList>
  );
};

export const CompetingClaimsSnapshot = () => {
  const { workspaceId } = useParams();

  const tasksQuery = useQuery(
    queryOptionsGetTasksPaginated({
      pageSize: HOME_VISIBLE_TASKS,
      sortBy: defaultTaskSortBy(TaskTypeEnum.CompetingClaim),
      type: TaskTypeEnum.CompetingClaim,
      workspaceId,
    }),
  );

  // Resolved claims stay listed but aren't counted — see `countExcludedStatuses`.
  const excludedCounts = useExcludedTaskCounts(workspaceId);

  return (
    <SingleQueryResults
      loadingFallback={<CompetingClaimsSkeleton />}
      query={tasksQuery}
    >
      {(tasksResponse) => {
        if (!excludedCounts) return <CompetingClaimsSkeleton />;

        const tasks = (tasksResponse?.items as CompetingClaimTask[]) || [];
        const totalTasks = tasksResponse?.meta?.totalItems || 0;
        const excludedTasks = excludedCounts.get(TASK_TYPE) ?? 0;

        return (
          <CompetingClaimContent
            countedTaskCount={totalTasks - excludedTasks}
            tasks={tasks}
            totalTasks={totalTasks}
          />
        );
      }}
    </SingleQueryResults>
  );
};
